import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { toast } from 'sonner'
import { resolveBranding } from '@feed-widget/branding'
import { setBrandingPath } from '@feed-widget/branding/editor'
import { getAllSurfaceTokenPaths } from '@/config/surfaceTokenManifests'
import { getBaselineTokenPaths } from '@/config/brandingTokens'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import { useOrganization } from '../hooks/useOrganization'
import { organizationApi, surfacesApi } from '../services/api'
import {
  Button,
  ConfirmDialog,
  FormSection,
  IconButton,
  PageHeader,
  PageLayout,
  SegmentedControl,
  StatePanel,
} from '../components/common'
import SurfaceRail from '../components/design/SurfaceRail'
import SurfacePreviewCanvas from '../components/design/SurfacePreviewCanvas'
import SurfaceInspector from '../components/design/SurfaceInspector'
import SurfaceCreateModal from '../components/design/SurfaceCreateModal'
import GeneratedPostsPolicyForm from '../components/feeds/GeneratedPostsPolicyForm'
import GeneratedPostAppearanceForm from '../components/feeds/GeneratedPostAppearanceForm'
import { GENERATED_POST_TYPE_PARAM, GENERATED_POST_TYPES } from '../components/feeds/generatedPostsPolicy'
import { useUnsavedChangesGuard } from '../components/post-creator/composerShared'
import styles from './DesignWorkspacePage.module.css'

const EMPTY_OVERRIDES = Object.freeze({})
const RANKING_DEFAULTS = {
  time_decay: { enabled: true, weight: 1.5 },
  odds: { enabled: true, weight: 1.0 },
  popularity: { enabled: true, weight: 1.2 },
  engagement: { enabled: true, weight: 1.0 },
  bet_status: { enabled: true, weight: 0.8 },
  media: { enabled: true, weight: 0.6 },
  creator: { enabled: true, weight: 0.8 },
}

const RANKING_FACTORS = [
  { key: 'time_decay', label: 'Recency' },
  { key: 'odds', label: 'Odds' },
  { key: 'popularity', label: 'Popularity' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'bet_status', label: 'Bet status' },
  { key: 'media', label: 'Media' },
  { key: 'creator', label: 'Creator' },
]

// Every section is internal-only; generated posts stay hidden from operator
// roles while the feature is in progress.
const CONFIGURATION_SECTIONS = Object.freeze([
  { value: 'surfaces', label: 'Surfaces', minRole: ROLES.INTERNAL_ORG_ADMIN },
  { value: 'ranking', label: 'Feed ranking', minRole: ROLES.INTERNAL_ORG_ADMIN },
  { value: 'generated', label: 'Generated posts', minRole: ROLES.INTERNAL_ORG_ADMIN },
])

function mergeRankingFactors(settings) {
  const nextValue = structuredClone(RANKING_DEFAULTS)
  const storedFactors = settings?.scoring?.factors || {}
  Object.entries(storedFactors).forEach(([key, value]) => {
    if (nextValue[key]) nextValue[key] = { ...nextValue[key], ...value }
  })
  return nextValue
}


/**
 * DesignWorkspacePage
 * -------------------
 * Single workspace for branding + per-surface design. Replaces the old
 * /dashboard/feed-design monolith and /dashboard/feeds surface CRUD.
 *
 * Layout: SurfaceRail (left) | SurfacePreviewCanvas (center) | SurfaceInspector (right)
 *
 * Selection can be 'baseline' (org-level branding) or a surface id (per-
 * surface overrides + settings). Dirty edits stage locally until the save
 * bar is actioned; the preview re-renders from the draft immediately.
 */
function BrandSurfaceConfigurationPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const canViewPrepacks = hasProductAccess(PRODUCT_KEYS.PREPACKS, 'view')
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useSearchParams()
  const requestedSurfaceSlug = searchParams.get('surface')
  const [selectedId, setSelectedId] = useState('baseline')
  const [brandingState, setBrandingState] = useState({
    draft: null, // null until fetched
    baseline: null,
    editVersion: 0,
    submission: null,
  })
  const brandingDraft = brandingState.draft
  const [overridesDrafts, setOverridesDrafts] = useState({}) // { [surfaceId]: { overrides, dirty } }
  const [modalState, setModalState] = useState({ open: false, mode: 'create', surface: null })
  const [pendingDelete, setPendingDelete] = useState(null)
  const [confirmDiscard, setConfirmDiscard] = useState(null)
  const [railCollapsed, setRailCollapsed] = useState(false)
  const [mobilePane, setMobilePane] = useState('preview')
  const [generatedPolicyDirty, setGeneratedPolicyDirty] = useState(false)
  const [generatedAppearanceDirty, setGeneratedAppearanceDirty] = useState({})
  const generatedDirty = generatedPolicyDirty || Object.values(generatedAppearanceDirty).some(Boolean)
  // Stable per kind, so the editors' dirty effects do not re-run each render.
  const appearanceDirtyHandlers = useMemo(() => Object.fromEntries(GENERATED_POST_TYPES.map((type) => [
    type.kind,
    (dirty) => setGeneratedAppearanceDirty((current) => (
      Boolean(current[type.kind]) === dirty ? current : { ...current, [type.kind]: dirty }
    )),
  ])), [])
  const [contentDirty, setContentDirty] = useState(false)
  const [inspectorDraftVersion, setInspectorDraftVersion] = useState(0)
  const [inspectorFocus, setInspectorFocus] = useState(null) // { label, paths, nonce }
  const [previewContentChoice, setPreviewContentChoice] = useState(null)
  const [previewFlash, setPreviewFlash] = useState(null) // { path, nonce }
  const [rankingFactors, setRankingFactors] = useState(() => mergeRankingFactors(currentOrg?.settings))
  const surfacePatchQueue = useRef(Promise.resolve())
  const orgId = currentOrg?.id
  const orgSlug = currentOrg?.slug

  const roleRank = ROLE_HIERARCHY[currentOrg?.role] || 0
  const canEditSurfaces = roleRank >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const availableSections = useMemo(
    () => CONFIGURATION_SECTIONS.filter((section) => roleRank >= ROLE_HIERARCHY[section.minRole]),
    [roleRank],
  )
  const defaultSection = availableSections[0]?.value
  const requestedSection = searchParams.get('section')
  const configurationSection = availableSections.some((section) => section.value === requestedSection)
    ? requestedSection
    : defaultSection

  const setConfigurationSection = useCallback((nextSection) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (nextSection === defaultSection) next.delete('section')
      else next.set('section', nextSection)
      if (nextSection !== 'generated') next.delete(GENERATED_POST_TYPE_PARAM)
      return next
    }, { replace: true })
  }, [defaultSection, setSearchParams])

  const {
    data: brandingData,
    dataUpdatedAt: brandingUpdatedAt,
    isFetching: brandingFetching,
    isLoading: brandingLoading,
    isError: brandingError,
    refetch: refetchBranding,
  } = useQuery({
    queryKey: ['branding', orgId],
    queryFn: () => organizationApi.getBranding(orgId),
    enabled: !!orgId && canEditSurfaces,
  })
  // Equal responses can share data objects and millisecond timestamps. The
  // cache's success count still distinguishes a read made after an uncertain save.
  const brandingReadVersion = queryClient.getQueryState(['branding', orgId])?.dataUpdateCount ?? 0

  const {
    data: surfacesData,
    isLoading: surfacesLoading,
    isError: surfacesError,
    refetch: refetchSurfaces,
  } = useQuery({
    queryKey: ['surfaces', orgSlug],
    queryFn: () => surfacesApi.list(orgSlug),
    enabled: !!orgSlug && canEditSurfaces,
  })

  const surfaces = useMemo(() => surfacesData?.items || [], [surfacesData])

  // Every selection change goes through here: a change of the active editor
  // invalidates any in-flight click-to-edit focus or locate-flash request.
  // The keyed inspector remounts on selection and replays its focusRequest
  // effect, so a stale request left behind (e.g. after deleting the surface
  // it was aimed at) would open, scroll, and steal focus for a field the
  // operator never picked on this selection.
  const applySelection = useCallback((nextId) => {
    setContentDirty(false)
    setInspectorFocus(null)
    setPreviewFlash(null)
    setSelectedId(nextId)
  }, [])

  useEffect(() => {
    if (!requestedSurfaceSlug || surfacesLoading) return
    const requestedSurface = surfaces.find((surface) => surface.slug === requestedSurfaceSlug)
    if (requestedSurface) applySelection(requestedSurface.id)
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete('surface')
    setSearchParams(nextSearchParams, { replace: true })
  }, [
    applySelection,
    requestedSurfaceSlug,
    searchParams,
    setSearchParams,
    surfaces,
    surfacesLoading,
  ])
  const selectedSurface = useMemo(
    () => (selectedId === 'baseline' ? null : surfaces.find((s) => s.id === selectedId) || null),
    [selectedId, surfaces]
  )
  const previewContentMode = canViewPrepacks && selectedSurface?.prepacks_enabled
    ? (previewContentChoice?.surfaceId === selectedSurface.id
      ? previewContentChoice.mode
      : selectedSurface.slug?.startsWith('prepacks-') ? 'prepacks' : 'feed')
    : 'feed'

  const handlePreviewContentChange = (mode) => {
    setPreviewContentChoice({ surfaceId: selectedSurface?.id, mode })
    setInspectorFocus(null)
    setPreviewFlash(null)
  }

  // Refresh clean drafts, but preserve edits made while a save/refetch was pending.
  useEffect(() => {
    if (!brandingData) return
    const nextBaseline = resolveBranding(brandingData)
    setBrandingState((current) => {
      const editedAfterSubmit = current.submission
        && current.editVersion !== current.submission.editVersion
      const draft = !current.draft || (!editedAfterSubmit && sameConfigValue(current.draft, current.baseline))
        ? nextBaseline : current.draft
      // A failed response does not prove the write failed. Only a fresh read
      // matching the draft can release its newer intent without an explicit discard.
      const confirmedAfterFailure = !brandingFetching && current.submission?.phase === 'uncertain'
        && brandingReadVersion > current.submission.readVersion
      const submission = confirmedAfterFailure && sameConfigValue(draft, nextBaseline) ? null : current.submission
      return { ...current, draft, baseline: nextBaseline, submission }
    })
  }, [brandingData, brandingUpdatedAt, brandingFetching, brandingReadVersion])

  // Rebase clean override drafts from the server while leaving active edits alone.
  useEffect(() => {
    setOverridesDrafts((previousDrafts) => reconcileOverrideDrafts(previousDrafts, surfaces))
  }, [surfaces])

  // Auto-select a reasonable default once data arrives
  useEffect(() => {
    if (selectedId !== 'baseline' && !selectedSurface && surfaces.length > 0) {
      applySelection('baseline')
    }
  }, [applySelection, selectedId, selectedSurface, surfaces])

  const resolvedBaseline = useMemo(
    () => (brandingDraft ? brandingDraft : resolveBranding({})),
    [brandingDraft]
  )

  // Dirty tracking — compared shallowly; for baseline vs brandingData we use JSON stringify.
  const baselineDirty = useMemo(() => {
    if (!brandingDraft || !brandingData) return false
    // A pending or uncertain save may replace the old server value, so returning
    // to that value is still a newer edit until the write outcome is reconciled.
    if (brandingState.submission && !sameConfigValue(brandingDraft, brandingState.submission.submitted)) return true
    return JSON.stringify(resolveBranding(brandingData)) !== JSON.stringify(brandingDraft)
  }, [brandingDraft, brandingData, brandingState.submission])

  const surfaceDirty = useMemo(() => {
    if (!selectedSurface) return false
    return !sameOverrides(
      getSurfaceThemeOverrides(selectedSurface),
      getDraftOverrides(overridesDrafts, selectedSurface),
    )
  }, [selectedSurface, overridesDrafts])

  const isDirty = selectedId === 'baseline' ? baselineDirty : surfaceDirty
  const hasUnsavedChanges = isDirty || contentDirty || generatedDirty
  useUnsavedChangesGuard(
    hasUnsavedChanges,
    'You have unsaved surface changes. Leave this page?',
  )

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    setRankingFactors(mergeRankingFactors(currentOrg?.settings))
  }, [currentOrg?.settings])

  /* ---- Mutations ---- */

  const updateBrandingMutation = useMutation({
    mutationFn: (branding) => organizationApi.updateBranding(orgId, branding),
    onMutate: () => queryClient.cancelQueries({ queryKey: ['branding', orgId], exact: true }),
    onSuccess: async (_response, submittedBranding) => {
      // The PATCH returns only an acknowledgement. Record its submitted snapshot
      // as saved before refetching, and exclude reads that began before the save.
      await queryClient.cancelQueries({ queryKey: ['branding', orgId], exact: true })
      const savedBaseline = resolveBranding(submittedBranding)
      setBrandingState((current) => ({
        ...current,
        baseline: savedBaseline,
        draft: current.editVersion === current.submission?.editVersion ? savedBaseline : current.draft,
      }))
      queryClient.setQueryData(['branding', orgId], submittedBranding)
      toast.success('Baseline branding saved')
      return queryClient.invalidateQueries({ queryKey: ['branding', orgId], exact: true })
    },
    onError: async (err) => {
      // A read begun before the failed response can still describe the old
      // server value. Exclude it before allowing a later read to reconcile.
      await queryClient.cancelQueries({ queryKey: ['branding', orgId], exact: true })
      const readVersion = queryClient.getQueryState(['branding', orgId])?.dataUpdateCount ?? 0
      setBrandingState((current) => ({
        ...current,
        submission: current.submission ? { ...current.submission, phase: 'uncertain', readVersion } : null,
      }))
      toast.error(err.response?.data?.error || 'Failed to save branding')
    },
    onSettled: (_data, error) => {
      if (!error) setBrandingState((current) => ({ ...current, submission: null }))
    },
  })

  const saveRankingMutation = useMutation({
    mutationFn: (nextFactors) => organizationApi.updateOrganizationSettings(orgId, {
      scoring: {
        ...(currentOrg?.settings?.scoring || {}),
        factors: nextFactors,
      },
    }),
    onSuccess: () => {
      toast.success('Live feed ranking updated')
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to update ranking'),
  })

  const uploadBannerMutation = useMutation({
    mutationFn: (file) => organizationApi.uploadBannerImage(orgId, file),
    onSuccess: (data) => {
      handleBrandingPathChange('banner.imageUrl', data.image_url)
      toast.success('Banner image uploaded')
      queryClient.invalidateQueries({ queryKey: ['branding', orgId] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to upload banner image')
    },
  })

  const surfaceOverridesMutation = useMutation({
    mutationFn: ({ surfaceId, overrides }) =>
      surfacesApi.update(orgSlug, surfaceId, { theme_overrides: overrides }),
    onSuccess: (updatedSurface, { surfaceId, overrides }) => {
      updateSurfaceQueryData(queryClient, orgSlug, updatedSurface)
      setOverridesDrafts((previousDrafts) => {
        const currentDraft = previousDrafts[surfaceId]
        if (!currentDraft?.dirty || !sameOverrides(currentDraft.overrides, overrides)) {
          return previousDrafts
        }
        return {
          ...previousDrafts,
          [surfaceId]: {
            overrides: getResponseThemeOverrides(updatedSurface, overrides),
            dirty: false,
          },
        }
      })
      toast.success('Surface overrides saved')
      queryClient.invalidateQueries({ queryKey: ['surfaces', orgSlug] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to save overrides')
    },
  })

  const surfacePatchMutation = useMutation({
    mutationFn: ({ surfaceId, patch }) => surfacesApi.update(orgSlug, surfaceId, patch),
    // Each queued write becomes optimistic only when it reaches the head of the
    // per-surface queue, so a failed rollback cannot erase a later config edit.
    onMutate: async ({ surfaceId, patch }) => {
      const key = ['surfaces', orgSlug]
      await queryClient.cancelQueries({ queryKey: key })
      const previousSurface = queryClient.getQueryData(key)?.items
        ?.find((surface) => surface.id === surfaceId)
      queryClient.setQueryData(key, (old) => {
        if (!old?.items) return old
        return {
          ...old,
          items: old.items.map((surface) => (
            surface.id === surfaceId ? { ...surface, ...patch } : surface
          )),
        }
      })
      return { previousSurface }
    },
    onSuccess: (updatedSurface, { surfaceId, patch }) => {
      updateSurfaceQueryData(queryClient, orgSlug, {
        id: surfaceId,
        ...patch,
        ...(updatedSurface || {}),
      })
    },
    onError: (err, { surfaceId, patch }, context) => {
      if (context?.previousSurface) {
        queryClient.setQueryData(['surfaces', orgSlug], (current) => {
          if (!current?.items) return current
          return {
            ...current,
            items: current.items.map((surface) => {
              if (surface.id !== surfaceId) return surface
              const restored = { ...surface }
              for (const key of Object.keys(patch)) {
                if (Object.prototype.hasOwnProperty.call(context.previousSurface, key)) {
                  restored[key] = context.previousSurface[key]
                } else {
                  delete restored[key]
                }
              }
              return restored
            }),
          }
        })
      }
      toast.error(err.response?.data?.error || 'Failed to update surface')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['surfaces', orgSlug], refetchType: 'none' })
    },
  })

  const enqueueSurfacePatch = (sourceSurface, patch) => {
    const request = createSurfacePatchRequest(sourceSurface, patch)
    const task = surfacePatchQueue.current
      .catch(() => undefined)
      .then(() => {
        const latestSurface = queryClient.getQueryData(['surfaces', orgSlug])?.items
          ?.find((surface) => surface.id === request.surfaceId)
        return surfacePatchMutation.mutateAsync({
          surfaceId: request.surfaceId,
          patch: materializeSurfacePatch(request, latestSurface),
        })
      })
    surfacePatchQueue.current = task.catch(() => undefined)
    return task
  }


  const createSurfaceMutation = useMutation({
    mutationFn: (payload) => surfacesApi.create(orgSlug, payload),
    onSuccess: (result, payload) => {
      toast.success('Surface created')
      if (result?.id) {
        const createdSurface = {
          theme_overrides: {},
          config: {},
          ...payload,
          ...result,
        }
        queryClient.setQueryData(['surfaces', orgSlug], (current) => {
          if (!current?.items) return current
          const alreadyListed = current.items.some((surface) => surface.id === result.id)
          return {
            ...current,
            items: alreadyListed
              ? current.items.map((surface) => (
                  surface.id === result.id ? { ...surface, ...createdSurface } : surface
                ))
              : [...current.items, createdSurface],
          }
        })
      }
      queryClient.invalidateQueries({ queryKey: ['surfaces', orgSlug] })
      setModalState({ open: false, mode: 'create', surface: null })
      if (result?.id) {
        applySelection(result.id)
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to create surface')
    },
  })

  const editSurfaceMutation = useMutation({
    mutationFn: ({ surfaceId, payload }) => surfacesApi.update(orgSlug, surfaceId, payload),
    onSuccess: () => {
      toast.success('Surface updated')
      queryClient.invalidateQueries({ queryKey: ['surfaces', orgSlug] })
      setModalState({ open: false, mode: 'create', surface: null })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to update surface')
    },
  })

  const deleteSurfaceMutation = useMutation({
    mutationFn: (surfaceId) => surfacesApi.delete(orgSlug, surfaceId),
    onSuccess: (result) => {
      if (result?.disabled) {
        toast.info(`Surface disabled (${result.targeted_posts || 'some'} posts still target it)`)
      } else {
        toast.success('Surface deleted')
      }
      queryClient.invalidateQueries({ queryKey: ['surfaces', orgSlug] })
      setPendingDelete(null)
      applySelection('baseline')
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to delete surface')
      setPendingDelete(null)
    },
  })

  /* ---- Handlers ---- */

  const trySelect = (nextId) => {
    if (hasUnsavedChanges) {
      setConfirmDiscard({ nextId })
      return
    }
    applySelection(nextId)
    setMobilePane('preview')
  }

  const tryCreateSurface = () => {
    if (hasUnsavedChanges) {
      setConfirmDiscard({ action: 'create' })
      return
    }
    setModalState({ open: true, mode: 'create', surface: null })
  }

  const handleBrandingPathChange = (path, value) => {
    setBrandingState((current) => {
      if (!current.draft) return current
      const draft = setBrandingPath(current.draft, path, value)
      const acceptsConfirmedBaseline = current.submission?.phase === 'uncertain'
        && brandingReadVersion > current.submission.readVersion && sameConfigValue(draft, current.baseline)
      return {
        ...current,
        editVersion: current.editVersion + 1,
        draft,
        submission: acceptsConfirmedBaseline ? null : current.submission,
      }
    })
  }

  const handleOverrideChange = (path, value) => {
    if (!selectedSurface) return
    setOverridesDrafts((previousDrafts) => {
      const previousOverrides = getDraftOverrides(previousDrafts, selectedSurface)
      const nextOverrides =
        value === undefined || value === '' || value === null
          ? deletePath(previousOverrides, path)
          : setPath(previousOverrides, path, value)
      return {
        ...previousDrafts,
        [selectedSurface.id]: {
          overrides: nextOverrides,
          dirty: !sameOverrides(nextOverrides, getSurfaceThemeOverrides(selectedSurface)),
        },
      }
    })
  }

  const inspectTokenPaths = useMemo(() => {
    if (selectedId === 'baseline') return getBaselineTokenPaths()
    if (selectedSurface) return getAllSurfaceTokenPaths(selectedSurface.display_type, previewContentMode)
    return []
  }, [selectedId, selectedSurface, previewContentMode])

  const handlePreviewTokenSelect = (selection) => {
    if (!selection) {
      setInspectorFocus(null)
      return
    }
    const { label, paths } = selection
    setInspectorFocus((previous) => ({ label, paths, nonce: (previous?.nonce || 0) + 1 }))
    setMobilePane('inspector')
  }

  const handleLocateToken = (path) => {
    setPreviewFlash((previous) => ({ path, nonce: (previous?.nonce || 0) + 1 }))
    setMobilePane('preview')
  }

  const handleSave = () => {
    if (selectedId === 'baseline' && brandingDraft) {
      setBrandingState((current) => ({
        ...current,
        submission: { phase: 'pending', editVersion: brandingState.editVersion, submitted: brandingDraft },
      }))
      updateBrandingMutation.mutate(brandingDraft)
    } else if (selectedSurface) {
      surfaceOverridesMutation.mutate({
        surfaceId: selectedSurface.id,
        overrides: getDraftOverrides(overridesDrafts, selectedSurface),
      })
    }
  }

  const handleDiscard = () => {
    if (selectedId === 'baseline') {
      const draft = resolveBranding(brandingData)
      // Discard yields ownership to a pending acknowledgement and releases any
      // uncertain submission because the newer edits were explicitly abandoned.
      setBrandingState((current) => ({
        ...current,
        draft,
        submission: current.submission?.phase === 'pending'
          ? { ...current.submission, editVersion: current.editVersion, submitted: draft }
          : null,
      }))
    } else if (selectedSurface) {
      setOverridesDrafts((previousDrafts) => ({
        ...previousDrafts,
        [selectedSurface.id]: {
          overrides: getSurfaceThemeOverrides(selectedSurface),
          dirty: false,
        },
      }))
    }
  }

  const discardAllDrafts = () => {
    setContentDirty(false)
    handleDiscard()
    if (selectedSurface) {
      setInspectorDraftVersion((version) => version + 1)
    }
  }

  const trySelectSection = (nextSection) => {
    if (nextSection === configurationSection) return
    if (configurationSection === 'generated' && generatedDirty) {
      setConfirmDiscard({ action: 'section', nextSection })
      return
    }
    setConfigurationSection(nextSection)
  }

  const handleConfirmDiscard = () => {
    if (confirmDiscard?.action === 'section') {
      setConfigurationSection(confirmDiscard.nextSection)
      setConfirmDiscard(null)
      return
    }
    discardAllDrafts()
    if (confirmDiscard?.nextId) {
      applySelection(confirmDiscard.nextId)
      setMobilePane('preview')
    } else if (confirmDiscard?.action === 'create') {
      setModalState({ open: true, mode: 'create', surface: null })
    }
    setConfirmDiscard(null)
  }

  /* ---- Render guards ---- */

  if (!orgSlug) {
    return (
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Configuration" mode="workspace" />
        <StatePanel
          state="restricted"
          title="No organization selected"
          description="Pick an organization to open Configuration."
        />
      </PageLayout>
    )
  }

  if (!defaultSection) {
    return (
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Configuration" context={currentOrg?.name || orgSlug} mode="workspace" />
        <StatePanel
          state="restricted"
          title="Wager admin access required"
          description="Feed Configuration is managed by the Wager team. Contact your Wager account manager to change it."
        />
      </PageLayout>
    )
  }

  if (canEditSurfaces && (brandingError || surfacesError)) {
    return (
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Configuration" context={currentOrg?.name || orgSlug} mode="workspace" />
        <StatePanel
          state="error"
          title="Configuration could not load"
          description="Your saved branding and surfaces were not changed."
          onRetry={() => {
            refetchBranding()
            refetchSurfaces()
          }}
        />
      </PageLayout>
    )
  }

  if (canEditSurfaces && (brandingLoading || surfacesLoading || !brandingDraft)) {
    return (
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Configuration" context={currentOrg?.name || orgSlug} mode="workspace" />
        <StatePanel
          state="loading"
          title="Loading Configuration…"
          description="Preparing the brand baseline, surfaces, and preview."
        />
      </PageLayout>
    )
  }

  /* ---- Selected display type for the canvas ---- */

  const previewDisplayType = selectedSurface
    ? selectedSurface.display_type
    : surfaces.find((s) => s.include_by_default)?.display_type ||
      surfaces[0]?.display_type ||
      'story_block'

  const previewOverrides = selectedSurface
    ? getDraftOverrides(overridesDrafts, selectedSurface)
    : null

  /* ---- Render ---- */

  const isSaving = updateBrandingMutation.isPending || surfaceOverridesMutation.isPending
  const saveActions = isDirty ? (
    <div className={styles.saveBar} data-testid="design-save-bar">
      <span className={styles.dirtyLabel}>Unsaved changes</span>
      <div className={styles.saveButtons}>
        <Button variant="secondary" onClick={handleDiscard} disabled={isSaving}>
          Discard
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Save changes
        </Button>
      </div>
    </div>
  ) : null

  return (
    <PageLayout
      size="workspace"
      className={styles.page}
      stickyActions={configurationSection === 'surfaces' ? saveActions : null}
    >
      <PageHeader
        title="Configuration"
        context={currentOrg?.name || orgSlug}
        mode="workspace"
      />
      <SegmentedControl
        className={styles.configurationSectionNav}
        label="Configuration section"
        value={configurationSection}
        onValueChange={trySelectSection}
        items={availableSections}
      />
      {configurationSection === 'surfaces' ? (
        <>
      <div className={styles.sectionBar}>
        <div className={styles.sectionActions}>
          <IconButton
            className={styles.railToggle}
            variant="secondary"
            onClick={() => setRailCollapsed((value) => !value)}
            aria-pressed={railCollapsed}
            label={railCollapsed ? 'Show surfaces rail' : 'Hide surfaces rail'}
            tooltip={railCollapsed ? 'Show surfaces' : 'Hide surfaces'}
            data-testid="rail-toggle"
          >
            {railCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </IconButton>
        </div>
      </div>

      <SegmentedControl
        className={styles.mobilePaneNav}
        label="Brand and surface workspace pane"
        value={mobilePane}
        onValueChange={(nextPane) => {
          if (nextPane === 'surfaces') setRailCollapsed(false)
          setMobilePane(nextPane)
        }}
        items={[
          { value: 'surfaces', label: 'Surfaces' },
          { value: 'preview', label: 'Preview' },
          { value: 'inspector', label: 'Inspector' },
        ]}
      />

      <div
        className={styles.workspace}
        data-rail-collapsed={railCollapsed}
        data-mobile-pane={mobilePane}
      >
        <aside
          className={styles.railColumn}
          aria-label="Surfaces"
          aria-hidden={railCollapsed}
          data-collapsed={railCollapsed}
          data-pane="surfaces"
        >
          <SurfaceRail
            surfaces={surfaces}
            selectedId={selectedId}
            onSelect={trySelect}
            onAddSurface={tryCreateSurface}
            dirty={hasUnsavedChanges}
          />
        </aside>

        <section className={styles.canvasColumn} aria-label="Live preview" data-pane="preview">
          <SurfacePreviewCanvas
            branding={resolvedBaseline}
            overrides={previewOverrides}
            displayType={previewDisplayType}
            surface={selectedSurface}
            organizationSlug={orgSlug}
            contentMode={previewContentMode}
            canViewPrepacks={canViewPrepacks}
            onContentModeChange={handlePreviewContentChange}
            inspectTokenPaths={inspectTokenPaths}
            onTokenSelect={handlePreviewTokenSelect}
            flashRequest={previewFlash}
          />
        </section>

        <aside className={styles.inspectorColumn} aria-label="Inspector" data-pane="inspector">
          {selectedId === 'baseline' ? (
            <SurfaceInspector
              key={`baseline:${inspectorDraftVersion}`}
              mode="baseline"
              branding={brandingDraft}
              onChange={handleBrandingPathChange}
              onUploadBanner={(file) => uploadBannerMutation.mutate(file)}
              isUploading={uploadBannerMutation.isPending}
              focusRequest={inspectorFocus}
              onLocateToken={handleLocateToken}
            />
          ) : selectedSurface ? (
            <SurfaceInspector
              key={`${selectedSurface.id}:${inspectorDraftVersion}`}
              mode="surface"
              surface={selectedSurface}
              contentMode={previewContentMode}
              onSaveSurface={(patch) => enqueueSurfacePatch(selectedSurface, patch)}
              overrides={getDraftOverrides(overridesDrafts, selectedSurface)}
              resolvedInherited={resolvedBaseline}
              onOverrideChange={handleOverrideChange}
              onSurfacePatch={(patch) => {
                enqueueSurfacePatch(selectedSurface, patch).catch(() => undefined)
              }}
              onDeleteSurface={() => setPendingDelete(selectedSurface)}
              onContentDirtyChange={setContentDirty}
              focusRequest={inspectorFocus}
              onLocateToken={handleLocateToken}
            />
          ) : (
            <StatePanel
              title="Surface missing"
              description="This surface was removed. Pick another from the left rail."
            />
          )}
        </aside>
      </div>
        </>
      ) : null}

      {configurationSection === 'ranking' ? (
      <section id="ranking" className={styles.rankingSection}>
        <FormSection
          eyebrow="Live feed"
          title="Ranking"
          description="These weights change production feed ordering for the organization."
        >
          <div className={styles.rankingList}>
            {RANKING_FACTORS.map((factor) => {
              const factorState = rankingFactors[factor.key] || RANKING_DEFAULTS[factor.key]
              return (
                <div key={factor.key} className={styles.rankingRow}>
                  <label className={styles.rankingMeta}>
                    <input
                      type="checkbox"
                      checked={Boolean(factorState.enabled)}
                      onChange={(event) => setRankingFactors((currentValue) => ({
                        ...currentValue,
                        [factor.key]: { ...currentValue[factor.key], enabled: event.target.checked },
                      }))}
                    />
                    <span>{factor.label}</span>
                  </label>
                  <input
                    type="range"
                    aria-label={`${factor.label} weight`}
                    min="0"
                    max="3"
                    step="0.1"
                    value={factorState.weight}
                    onChange={(event) => setRankingFactors((currentValue) => ({
                      ...currentValue,
                      [factor.key]: { ...currentValue[factor.key], weight: Number(event.target.value) },
                    }))}
                    className={styles.rankingSlider}
                  />
                  <span className={styles.rankingValue}>{Number(factorState.weight).toFixed(1)}</span>
                </div>
              )
            })}
            <div className={styles.rankingActions}>
              <Button onClick={() => saveRankingMutation.mutate(rankingFactors)} disabled={saveRankingMutation.isPending}>
                {saveRankingMutation.isPending ? 'Saving…' : 'Save live ranking'}
              </Button>
            </div>
          </div>
        </FormSection>
      </section>
      ) : null}

      {configurationSection === 'generated' ? (
        <section id="generated-posts" className={styles.generatedSection} aria-label="Generated posts">
          {/* Every org admin styles these cards, clients included: each editor
              saves only its own kind's tokens. */}
          <GeneratedPostsPolicyForm
            key={orgId}
            orgId={orgId}
            orgName={currentOrg?.name || orgSlug}
            onDirtyChange={setGeneratedPolicyDirty}
            renderAppearance={(type, { active }) => (
              <GeneratedPostAppearanceForm
                key={`${type.kind}:${orgId}`}
                kind={type.kind}
                active={active}
                orgId={orgId}
                onDirtyChange={appearanceDirtyHandlers[type.kind]}
                headingId={`${type.kind}-appearance-heading`}
                headingLevel={2}
              />
            )}
          />
        </section>
      ) : null}

      <SurfaceCreateModal
        open={modalState.open}
        mode={modalState.mode}
        initialSurface={modalState.surface}
        saving={createSurfaceMutation.isPending || editSurfaceMutation.isPending}
        onClose={() => setModalState({ open: false, mode: 'create', surface: null })}
        onSubmit={(payload) => {
          if (modalState.mode === 'edit' && modalState.surface) {
            editSurfaceMutation.mutate({ surfaceId: modalState.surface.id, payload })
          } else {
            createSurfaceMutation.mutate(payload)
          }
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete surface"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}"? If any posts target it, the surface will be soft-disabled instead so history stays intact.`
            : ''
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={() => pendingDelete && deleteSurfaceMutation.mutate(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={!!confirmDiscard}
        title="Discard unsaved changes?"
        message={confirmDiscard?.action === 'create'
          ? 'Adding a surface will switch the workspace and discard any unsaved branding, content, or appearance edits.'
          : confirmDiscard?.action === 'section'
            ? 'Switching sections will discard unsaved generated post settings.'
            : 'Switching surfaces will discard unsaved content and appearance edits.'}
        variant="danger"
        confirmLabel={confirmDiscard?.action === 'create' ? 'Discard and add surface' : 'Discard and switch'}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setConfirmDiscard(null)}
      />
    </PageLayout>
  )
}

export default function DesignWorkspacePage() {
  const { currentOrg } = useOrganization()
  return <BrandSurfaceConfigurationPage key={currentOrg?.id} />
}

/* ---- Path helpers for overrides (no auto-defaults) ---- */

function getSurfaceThemeOverrides(surface) {
  const overrides = surface?.theme_overrides
  return overrides && typeof overrides === 'object' && !Array.isArray(overrides)
    ? overrides
    : EMPTY_OVERRIDES
}

function getDraftOverrides(drafts, surface) {
  return drafts[surface.id]?.overrides || getSurfaceThemeOverrides(surface)
}

function getResponseThemeOverrides(surface, fallback) {
  return surface && Object.prototype.hasOwnProperty.call(surface, 'theme_overrides')
    ? getSurfaceThemeOverrides(surface)
    : fallback
}

function sameOverrides(first, second) {
  return first === second || JSON.stringify(first) === JSON.stringify(second)
}

function reconcileOverrideDrafts(drafts, surfaces) {
  let changed = Object.keys(drafts).length !== surfaces.length
  const nextDrafts = {}

  for (const surface of surfaces) {
    const currentDraft = drafts[surface.id]
    const overrides = getSurfaceThemeOverrides(surface)
    const draftIsDirty = currentDraft?.dirty && !sameOverrides(currentDraft.overrides, overrides)
    if (draftIsDirty) {
      nextDrafts[surface.id] = currentDraft
      continue
    }

    if (currentDraft && sameOverrides(currentDraft.overrides, overrides) && !currentDraft.dirty) {
      nextDrafts[surface.id] = currentDraft
      continue
    }

    nextDrafts[surface.id] = { overrides, dirty: false }
    changed = true
  }

  return changed ? nextDrafts : drafts
}

function updateSurfaceQueryData(queryClient, orgSlug, updatedSurface) {
  if (!updatedSurface?.id) return
  queryClient.setQueryData(['surfaces', orgSlug], (current) => {
    if (!current?.items) return current
    return {
      ...current,
      items: current.items.map((surface) => (
        surface.id === updatedSurface.id ? { ...surface, ...updatedSurface } : surface
      )),
    }
  })
}

function setPath(source, path, value) {
  const parts = path.split('.')
  const next = { ...(source || {}) }
  let cursor = next
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    cursor[key] = cursor[key] && typeof cursor[key] === 'object' ? { ...cursor[key] } : {}
    cursor = cursor[key]
  }
  cursor[parts[parts.length - 1]] = value
  return next
}

function deletePath(source, path) {
  if (!source || typeof source !== 'object') return source
  const parts = path.split('.')
  const next = { ...source }
  let cursor = next
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!cursor[key] || typeof cursor[key] !== 'object') return source
    cursor[key] = { ...cursor[key] }
    cursor = cursor[key]
  }
  delete cursor[parts[parts.length - 1]]
  return next
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function sameConfigValue(first, second) {
  return first === second || JSON.stringify(first) === JSON.stringify(second)
}

function collectConfigChanges(previous, next, prefix = '', changes = []) {
  const previousObject = isPlainObject(previous) ? previous : {}
  const nextObject = isPlainObject(next) ? next : {}
  const keys = new Set([...Object.keys(previousObject), ...Object.keys(nextObject)])

  for (const key of keys) {
    const path = prefix ? `${prefix}.${key}` : key
    if (!Object.prototype.hasOwnProperty.call(nextObject, key)) {
      changes.push({ path, remove: true })
      continue
    }
    const previousValue = previousObject[key]
    const nextValue = nextObject[key]
    if (isPlainObject(nextValue)) {
      if (isPlainObject(previousValue)) {
        if (!Object.keys(previousValue).length && !Object.keys(nextValue).length) continue
        collectConfigChanges(previousValue, nextValue, path, changes)
      } else if (Object.keys(nextValue).length) {
        collectConfigChanges({}, nextValue, path, changes)
      } else if (!sameConfigValue(previousValue, nextValue)) {
        changes.push({ path, value: nextValue })
      }
    } else if (!sameConfigValue(previousValue, nextValue)) {
      changes.push({ path, value: nextValue })
    }
  }

  return changes
}

function createSurfacePatchRequest(sourceSurface, patch) {
  const requestPatch = { ...(patch || {}) }
  const hasConfig = Object.prototype.hasOwnProperty.call(requestPatch, 'config')
  const nextConfig = isPlainObject(requestPatch.config) ? requestPatch.config : {}
  delete requestPatch.config
  return {
    surfaceId: sourceSurface.id,
    baseConfig: isPlainObject(sourceSurface.config) ? sourceSurface.config : {},
    configChanges: hasConfig
      ? collectConfigChanges(sourceSurface.config, nextConfig)
      : null,
    patch: requestPatch,
  }
}

function materializeSurfacePatch(request, latestSurface) {
  const patch = { ...request.patch }
  if (request.configChanges) {
    let config = isPlainObject(latestSurface?.config)
      ? latestSurface.config
      : request.baseConfig
    for (const change of request.configChanges) {
      config = change.remove
        ? deletePath(config, change.path)
        : setPath(config, change.path, change.value)
    }
    patch.config = config
  }
  return patch
}
